import { useMemo, useState } from 'react';
import Modal from '@/components/common/AppModal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Badge from 'react-bootstrap/Badge';
import SubtleBadge from '@/components/common/SubtleBadge';
import Icon from './Icon';
import { buildTreeModel } from '../utils/treeModel';
import {
  collectKeywordSet,
  compareKeywordSets,
  computeTreeStats
} from '../utils/treeStats';
import { decodeName } from '../utils/decodeName';
import type { ChangeEvent } from 'react';
import type { ClusterData, TreeModel } from '../types';

type SecondaryCluster = {
  stem: string;
  model: TreeModel;
  data: ClusterData;
};

type CompareModalProps = {
  show: boolean;
  onHide: () => void;
  primaryData?: ClusterData | null;
};

function isClusterData(value: unknown): value is ClusterData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'hierarchical' in value &&
    (value as { hierarchical?: unknown }).hierarchical !== undefined
  );
}

function normalizePayload(json: unknown): ClusterData {
  if (
    typeof json === 'object' &&
    json !== null &&
    'data' in json &&
    isClusterData((json as { data?: unknown }).data)
  ) {
    return (json as { data: ClusterData }).data;
  }
  if (isClusterData(json)) return json;
  throw new Error('ساختار JSON معتبر نیست (hierarchical پیدا نشد).');
}

function toSecondary(data: ClusterData, fallbackName = 'cluster-b'): SecondaryCluster {
  return {
    stem: decodeName(data.stem) || fallbackName,
    model: buildTreeModel(data.hierarchical),
    data
  };
}

export default function CompareModal({ show, onHide, primaryData }: CompareModalProps) {
  const [secondary, setSecondary] = useState<SecondaryCluster | null>(null);
  const [error, setError] = useState('');

  const primaryModel = useMemo(
    () =>
      primaryData?.hierarchical
        ? buildTreeModel(primaryData.hierarchical)
        : null,
    [primaryData]
  );

  const comparison = useMemo(() => {
    if (!primaryModel || !secondary?.model) return null;
    const a = collectKeywordSet(primaryModel.nodesById);
    const b = collectKeywordSet(secondary.model.nodesById);
    return {
      ...compareKeywordSets(a, b),
      statsA: computeTreeStats(primaryModel.nodesById),
      statsB: computeTreeStats(secondary.model.nodesById),
      stemA: decodeName(primaryData?.stem ?? ''),
      stemB: secondary.stem
    };
  }, [primaryData, primaryModel, secondary]);

  const loadSecondaryData = (json: unknown, fallbackName?: string) => {
    try {
      setError('');
      const data = normalizePayload(json);
      setSecondary(toSecondary(data, fallbackName));
    } catch (err) {
      setSecondary(null);
      setError(err instanceof Error ? err.message : 'خواندن داده با خطا مواجه شد');
    }
  };

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      loadSecondaryData(JSON.parse(text), file.name);
    } catch (err) {
      setSecondary(null);
      setError(err instanceof Error ? err.message : 'خواندن فایل با خطا مواجه شد');
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="cluster-compare-modal"
    >
      <style>{`
        .cluster-compare-modal {
          max-width: min(640px, calc(100vw - 1rem));
        }
        .cluster-compare-modal .btn-close {
          font-size: 0.65rem;
          padding: 0.4rem;
        }
      `}</style>
      <Modal.Header closeButton className="py-2">
        <Modal.Title className="d-flex align-items-center gap-2 fs-0">
          <Icon name="subtract" />
          مقایسه دو کلاستر
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>فایل JSON کلاستر دوم</Form.Label>
          <Form.Control
            type="file"
            accept="application/json,.json"
            onChange={onFile}
            size="sm"
          />
        </Form.Group>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        {comparison ? (
          <div>
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
              <Badge bg="primary" className="fs--1">
                A: {comparison.stemA}
              </Badge>
              <SubtleBadge
                bg="info"
                className="fs--1 d-inline-flex align-items-center"
                style={{ lineHeight: 1.2 }}
              >
                B: {comparison.stemB}
              </SubtleBadge>
            </div>
            <div className="d-flex flex-wrap gap-3 mb-3 fs--1">
              <div>
                A → خوشه {comparison.statsA.clusters} / کلمه{' '}
                {comparison.statsA.keywords}
              </div>
              <div>
                B → خوشه {comparison.statsB.clusters} / کلمه{' '}
                {comparison.statsB.keywords}
              </div>
            </div>
            <Row className="g-3">
              {[
                { title: 'مشترک', items: comparison.shared },
                { title: 'فقط A', items: comparison.onlyA },
                { title: 'فقط B', items: comparison.onlyB }
              ].map(section => (
                <Col md={4} key={section.title}>
                  <h6 className="fs--1 mb-2">
                    {section.title} ({section.items.length})
                  </h6>
                  <ul
                    className="list-unstyled border rounded-2 p-2 mb-0 overflow-auto fs--1 bg-body"
                    style={{ maxHeight: 240 }}
                  >
                    {section.items.slice(0, 40).map(item => (
                      <li key={`${section.title}-${item}`}>{item}</li>
                    ))}
                  </ul>
                </Col>
              ))}
            </Row>
          </div>
        ) : (
          <p className="text-muted mb-0 small">
            بعد از انتخاب فایل دوم، کلمات مشترک و اختصاصی هر کلاستر نمایش داده
            می‌شود.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer className="py-2">
        <Button variant="outline-secondary" size="sm" onClick={onHide}>
          بستن
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
